import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { ArrowLeft, Check, CheckCheck, Pencil } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import figmaReviewAPI from '../../services/figmaReviewService';

// One banner: its strings as the plugin pushed them, and one language at a time.
//
// One language at a time because there will be eighteen of them. A reviewer speaks one of those
// languages and can judge only that one, so the screen shows the English and a single translation
// side by side; the chips above carry the whole picture, so nothing is hidden, only unstacked.

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

const FigmaBannerPage = () => {
  const { bannerId } = useParams();
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  // Edits live in a map keyed by translation id; a row without an entry shows the stored wording.
  const [edits, setEdits] = useState({});
  // Identifies the most recent load, so responses that arrive out of order can be dropped.
  const latestLoadID = useRef(0);

  const load = useCallback(async () => {
    // A brand switch can put two loads in flight for the same banner id, and the older one
    // landing last would paint the brand the reviewer just left. Only the newest may write.
    const requestID = ++latestLoadID.current;
    setError('');
    try {
      const { data } = await figmaReviewAPI.getBanner(bannerId);
      if (requestID !== latestLoadID.current) return;
      setDetail(data);
      setEdits({});
      // Land the reviewer on the language that actually needs them, falling back to the first one
      // that exists, so the screen is never empty when there is something to see.
      setLanguage((current) => {
        const languages = data.banner?.languages || [];
        if (current && languages.some((l) => l.language === current)) return current;
        return (languages.find((l) => l.pending > 0) || languages[0])?.language || null;
      });
    } catch (err) {
      if (requestID !== latestLoadID.current) return;
      setError(err.response?.data?.error || 'Failed to load the banner');
    } finally {
      if (requestID === latestLoadID.current) setLoading(false);
    }
  }, [bannerId]);

  // Banner and translation ids are brand-scoped, so a brand change invalidates everything on
  // screen — back to a blank page rather than copy the new brand's approvals must not touch.
  // (The reload after approving deliberately does not pass through here: it refreshes in place,
  // without blanking rows the reviewer is still working through.)
  useEffect(() => {
    setLoading(true);
    setDetail(null);
    load();
  }, [load, activeBrand]);

  const banner = detail?.banner;
  const languages = useMemo(() => banner?.languages || [], [banner]);

  // One row per string, carrying the selected language's translation when it has one. Strings with
  // nothing in this language stay on screen -- "not translated yet" is information the reviewer
  // needs, and dropping the row would make the banner look shorter than it is.
  const rows = useMemo(() => {
    if (!detail) return [];
    return detail.strings.map((str) => ({
      ...str,
      translation: (str.translations || []).find((t) => t.language === language) || null,
    }));
  }, [detail, language]);

  const pendingRows = rows.filter((row) => row.translation?.status === 'pending');

  const textOf = (row) => edits[row.translation.id] ?? row.translation.text;
  const edited = (row) =>
    edits[row.translation.id] !== undefined && edits[row.translation.id] !== row.translation.text;

  const approve = async (approvals) => {
    try {
      await figmaReviewAPI.approve(approvals);
      await load();
    } catch (err) {
      // The server names the row when the queue moved under the reviewer; the reload that follows
      // already shows reality.
      setError(err.response?.data?.error || 'Failed to approve');
    }
  };

  const approveOne = async (row) => {
    if (busyId || busyAll) return;
    setBusyId(row.translation.id);
    setError('');
    await approve([{ translation_id: row.translation.id, text: textOf(row).trim() }]);
    setBusyId(null);
  };

  const approveAll = async () => {
    if (busyId || busyAll || pendingRows.length === 0) return;
    setBusyAll(true);
    setError('');
    // Every waiting row as it stands on screen — edits included: a reviewer who fixed two wordings
    // and then bulk-approves the rest expects all of it to land in one go.
    await approve(pendingRows.map((row) => ({
      translation_id: row.translation.id,
      text: textOf(row).trim(),
    })));
    setBusyAll(false);
  };

  if (loading) {
    return (
      <div className="max-w-[1100px] mx-auto py-6 flex justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!banner) {
    return (
      <div className="max-w-[1100px] mx-auto py-6 space-y-4">
        <Button variant="outline" className="gap-1.5" onClick={() => navigate('/figma-review')}>
          <ArrowLeft className="h-4 w-4" />Back to banners
        </Button>
        <Alert variant="error">
          <AlertDescription>{error || 'That banner no longer exists.'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto py-6">
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate('/figma-review')}
                className="p-1.5 rounded-md text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors mt-0.5"
                aria-label="Back to banners"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div>
                <h2 className="text-xl font-bold">{banner.frame_name}</h2>
                <p className="text-xs text-ink-dim">
                  {banner.file_name} · {banner.string_count} string{banner.string_count === 1 ? '' : 's'} ·
                  {' '}last pushed {fmtDate(banner.last_seen_at)}
                </p>
              </div>
            </div>

            {pendingRows.length > 0 && (
              <Button className="gap-1.5" onClick={approveAll} disabled={busyId !== null || busyAll}>
                {busyAll ? <Spinner size="sm" /> : <CheckCheck className="h-4 w-4" />}
                Approve all {pendingRows.length} as shown
              </Button>
            )}
          </div>

          {languages.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-3 border-t border-white/5">
              <span className="text-[11px] text-ink-mute mr-1">Language:</span>
              {languages.map((entry) => {
                const active = entry.language === language;
                let variant = 'outline';
                if (active) variant = 'default';
                else if (entry.pending > 0) variant = 'warning';
                return (
                  <button key={entry.language} type="button" onClick={() => setLanguage(entry.language)}>
                    <Badge variant={variant} className="cursor-pointer uppercase">
                      {entry.language}
                      {entry.pending > 0 ? ` · ${entry.pending} waiting` : ' · done'}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-4 space-y-4">
          {languages.length === 0 ? (
            <p className="text-sm text-ink-dim text-center py-8">
              This banner has no translations yet. They appear the first time a designer generates a
              language from the plugin.
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={row.occurrence_id}
                className="rounded-lg border border-white/10 bg-surface-2/40 p-4 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-ink-mute font-medium truncate">
                    {row.collection} · {row.name}
                  </div>
                  {row.translation && edited(row) && (
                    <Badge variant="warning" className="shrink-0 gap-1">
                      <Pencil className="h-3 w-3" /> becomes the global wording
                    </Badge>
                  )}
                  {row.translation?.status === 'approved' && !edited(row) && (
                    <Badge variant="success" className="shrink-0">approved</Badge>
                  )}
                </div>

                <div className="text-sm text-ink font-medium">{row.source_text}</div>

                {!row.translation ? (
                  <p className="text-sm text-ink-mute">Not translated into this language yet.</p>
                ) : (
                  <>
                    <Textarea
                      className="min-h-[60px]"
                      rows={Math.min(4, Math.max(2, Math.ceil(textOf(row).length / 70)))}
                      value={textOf(row)}
                      maxLength={5000}
                      onChange={(e) =>
                        setEdits((prev) => ({ ...prev, [row.translation.id]: e.target.value }))}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant={row.translation.status === 'approved' ? 'outline' : 'default'}
                        disabled={(busyId !== null && busyId !== row.translation.id) || busyAll}
                        onClick={() => approveOne(row)}
                      >
                        {busyId === row.translation.id ? (
                          'Approving…'
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1.5" />
                            {row.translation.status === 'approved' ? 'Re-approve' : 'Approve'}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FigmaBannerPage;
